import { Injectable } from "@angular/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  ClientPromiseLookupService,
  AccountPromiseLookupService,
  UserPromiseLookupService,
  CcyPairPromiseLookupService,
  TraderGroupPromiseLookupService,
  CcyPairLookupService,
  TraderGroupLookupService,
  AlgoStrategyPromiseLookupService,
  FixingSourcePromiseLookupService,
  UserLookupService,
  BranchLookupService,
  ClientLookupService,
  AccountLookupService,
  ClientAccountVisibilityType,
  OrderDataLookupService,
} from "shared/cubiq-services";
import { Subject } from "rxjs";
import { OENEntryType } from "../../data/enums/oe-entry-type.enum";
import { ICAutocompleteClientModel } from "../../interface/controls/models/bfx-interface-controls.models";

@Injectable()
export class OELookupService {
  /**********************************************************************/
  /* CONSTRUCTOR */

  constructor(
    private clientLookup: ClientPromiseLookupService,
    private accountLookup: AccountPromiseLookupService,
    private userLookup: UserPromiseLookupService,
    private ccyPairLookup: CcyPairPromiseLookupService,
    private traderGroupLookup: TraderGroupPromiseLookupService,
    private algoLookup: AlgoStrategyPromiseLookupService,
    private fixingSourceLookup: FixingSourcePromiseLookupService,
    private branchLookupService: BranchLookupService,
    private orderDataLookup: OrderDataLookupService,
  ) {}

  /**********************************************************************/
  /* CLIENT */
  public client(
    clientId: string,
    active: boolean = true,
    type?: ClientAccountVisibilityType | null,
  ): Promise<CubiqTS.ClientImpl> {
    return new Promise<CubiqTS.ClientImpl>((res, rej) => {
      if (clientId != null && clientId !== "") {
        // Client Account
        let promise = active
          ? this.clientLookup.getActiveClient(clientId, type)
          : this.clientLookup.getClient(clientId, type);

        promise.then((clientData: CubiqTS.ClientImpl) => {
          if (clientData != null && clientData instanceof CubiqTS.ClientImpl) {
            res(clientData);
          } else {
            rej(`Client with clientId: '${clientId}' not found.`);
          }
        });
      } else {
        rej(`Invalid clientId: '${clientId}'`);
      }
    });
  }

  public clientBranchMap(
    data: Array<ICAutocompleteClientModel | CubiqTS.ClientImpl>,
  ): Promise<Map<string, CubiqTS.BranchImpl>> {
    let branchCdSet = new Set<string>();

    if (Array.isArray(data))
      data.forEach((c) => {
        if (c != null && c.branchCd != null && branchCdSet.has(c.branchCd) === false) {
          branchCdSet.add(c.branchCd);
        }
      });

    return new Promise((res, rej) => {
      let branchMap = new Map<string, CubiqTS.BranchImpl>();
      let branchPromises = [...branchCdSet.values()].map((branchCd) =>
        this.branchLookupService.getBranch(branchCd),
      );

      Promise.all(branchPromises).then(
        (branches) => {
          branches.forEach((branch) => {
            branchMap.set(branch.branchId, branch);
          });
          res(branchMap);
        },
        (err) => {
          rej(err);
        },
      );
    });
  }

  /* NO PROMISES */

  public clientNow(clientId: string): CubiqTS.ClientImpl {
    if (typeof clientId === "string") {
      let clientLookupNow: ClientLookupService = this.clientLookup.getBase();
      return clientLookupNow.getActiveClient(clientId);
    }
    return null;
  }

  public clientsForAccountNow(accountId: string | CubiqTS.AccountImpl): Array<CubiqTS.ClientImpl> {
    if (accountId instanceof CubiqTS.AccountImpl) {
      accountId = accountId.id;
    }

    if (typeof accountId === "string") {
      let accountLookupNow: AccountLookupService = this.accountLookup.getBase();
      return accountLookupNow.getClientsForAccount(accountId);
    }
    return null;
  }

  public clientsForBreakAccountNow(
    account: string | CubiqTS.AccountImpl,
  ): Array<CubiqTS.ClientImpl> {
    if (typeof account === "string") {
      account = this.accountNow(account);
    }
    if (account instanceof CubiqTS.AccountImpl && account.salesGroupId instanceof CubiqTS.Long) {
      let clientLookupNow: ClientLookupService = this.clientLookup.getBase();
      return clientLookupNow.lookupClientsForSalesGroup(account.salesGroupId, 0);
    }
    return null;
  }

  public clientsForOrderEntryNow(
    entryType: OENEntryType,
    token: string,
    resultSize: number,
  ): Array<CubiqTS.ClientImpl> {
    let clientLookupNow: ClientLookupService = this.clientLookup.getBase();
    if (clientLookupNow != null) {
      switch (entryType) {
        case OENEntryType.ALGO:
          return clientLookupNow.lookupActiveAFillClients(token, resultSize, true);
        case OENEntryType.BENCHMARK:
          return clientLookupNow.lookupActiveBMarkClients(token, resultSize, true);
        default:
          return clientLookupNow.lookupActiveClients(token, resultSize, true);
      }
    }
    return null;
  }

  public clientBranchMapNow(
    data: Array<ICAutocompleteClientModel | CubiqTS.ClientImpl>,
  ): Map<string, CubiqTS.BranchImpl> {
    let branchCdSet = new Set<string>();
    if (Array.isArray(data) && data.length > 0) {
      for (let item of data) {
        if (item != null && item.branchCd != null && branchCdSet.has(item.branchCd) === false) {
          branchCdSet.add(item.branchCd);
        }
      }
    }

    let branchMap = new Map<string, CubiqTS.BranchImpl>();
    if (branchCdSet != null && branchCdSet.size > 0) {
      for (let branchCd of branchCdSet) {
        let branch = this.branchLookupService.getBranch(branchCd);
        if (branch instanceof CubiqTS.BranchImpl) {
          branchMap.set(branch.branchId, branch);
        }
      }
      return branchMap;
    }
    return null;
  }

  public getClientSubject(): Subject<CubiqTS.ConcreteEncodable> {
    return this.clientLookup.getModelUpdateSubject();
  }

  public areMarketOrdersAllowedForClient(client: CubiqTS.ClientImpl | string): boolean {
    let value: boolean = false;
    let clientData = typeof client === "string" ? this.clientNow(client) : client;
    if (clientData instanceof CubiqTS.ClientImpl) {
      let branch = this.branchLookupService.getItem(clientData.branchCd);
      value = branch instanceof CubiqTS.BranchImpl && branch.allowMarketOrdersFg;
    }
    return value;
  }

  /**********************************************************************/
  /* ACCOUNT  */

  public account(
    accountId: string,
    active: boolean = true,
    type?: ClientAccountVisibilityType | null,
  ): Promise<CubiqTS.AccountImpl> {
    return new Promise<CubiqTS.AccountImpl>((res, rej) => {
      if (accountId != null && accountId !== "") {
        // Client Account
        let promise = active
          ? this.accountLookup.getActiveAccount(accountId, type)
          : this.accountLookup.getAccount(accountId, type);

        promise.then((accountData: CubiqTS.AccountImpl) => {
          if (accountData != null && accountData instanceof CubiqTS.AccountImpl) {
            res(accountData);
          } else {
            rej(`Account with accountId: '${accountId}' not found.`);
          }
        });
      } else {
        rej(`Invalid accountId: '${accountId}'`);
      }
    });
  }

  public getAccountSubject(): Subject<CubiqTS.ConcreteEncodable> {
    return this.accountLookup.getModelUpdateSubject();
  }

  public accountNow(accountId: string): CubiqTS.AccountImpl {
    if (typeof accountId == "string" && accountId !== "") {
      let accountLookupNow: AccountLookupService = this.accountLookup.getBase();
      return accountLookupNow.getItem(accountId);
    }
    return null;
  }

  accountWithVisibilityNow(
    accountId: string,
    type: ClientAccountVisibilityType | null,
  ): CubiqTS.AccountImpl {
    if (typeof accountId == "string" && accountId !== "") {
      let accountLookupNow: AccountLookupService = this.accountLookup.getBase();
      return accountLookupNow.getActiveAccount(accountId, type);
    }
    return null;
  }

  public accountsForClientIncludingBreakNow(
    client: string | CubiqTS.ClientImpl,
    onlyFullVisibility: boolean = false,
  ): Array<CubiqTS.AccountImpl> {
    let clientImpl: CubiqTS.ClientImpl =
      client instanceof CubiqTS.ClientImpl
        ? client
        : typeof client === "string"
        ? this.clientNow(client)
        : null;
    if (clientImpl instanceof CubiqTS.ClientImpl) {
      let accountLookupNow: AccountLookupService = this.accountLookup.getBase();
      return accountLookupNow.getActiveBreakAndClientAccounts(
        clientImpl.clientId,
        clientImpl.salesGroupId.toString(),
        onlyFullVisibility,
      );
    }

    return null;
  }

  public accountsForOrderEntryNow(
    entryType: OENEntryType,
    token: string,
    resultSize: number,
  ): Array<CubiqTS.AccountImpl> {
    let accountLookupNow: AccountLookupService = this.accountLookup.getBase();
    if (accountLookupNow != null) {
      switch (entryType) {
        case OENEntryType.BENCHMARK:
          return accountLookupNow.lookupActiveBMAccounts(token, resultSize, true);
        default:
          return accountLookupNow.lookupActiveAccounts(token, resultSize, true);
      }
    }
    return null;
  }

  /**********************************************************************/
  /* BREAK & CLIENT ACCOUNTs  */

  public breakAndClientAccounts(
    client: string | CubiqTS.ClientImpl,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      if (client != null && typeof client === "string" && client !== "") {
        this.client(client).then((clientResult: CubiqTS.ClientImpl) => {
          this.executeLookupBreakAndClientAccounts(clientResult).then(
            (result) => {
              res(result);
            },
            (err) => {
              rej(err);
            },
          );
        });
      } else if (client != null && client instanceof CubiqTS.ClientImpl) {
        this.executeLookupBreakAndClientAccounts(client).then(
          (result) => {
            res(result);
          },
          (err) => {
            rej(err);
          },
        );
      } else {
        rej(`Invalid clientId: '${client}'`);
      }
    });
  }

  private executeLookupBreakAndClientAccounts(
    client: CubiqTS.ClientImpl,
  ): Promise<Array<CubiqTS.AccountImpl>> {
    return new Promise<Array<CubiqTS.AccountImpl>>((res, rej) => {
      let clientId: string = client.clientId;
      let clientSalesGroup: string = client.salesGroupId.toString();
      this.accountLookup.getActiveBreakAndClientAccounts(clientId, clientSalesGroup).then(
        (resultSet) => {
          if (resultSet != null && resultSet instanceof Array && resultSet.length > 0) {
            res(resultSet);
          } else {
            rej("No data");
          }
        },
        (err) => {
          rej(err);
        },
      );
    });
  }

  public userById(userId: CubiqTS.Long): Promise<CubiqTS.UserImpl> {
    return new Promise<CubiqTS.UserImpl>((res, rej) => {
      if (userId != null && userId instanceof CubiqTS.Long) {
        this.userLookup.getItem(userId.toString()).then((userData: CubiqTS.UserImpl) => {
          if (userData != null) {
            res(userData);
          } else {
            rej(`User with userId: '${userId.getLowBits()}' not found.`);
          }
        });
      } else {
        res(null);
      }
    });
  }

  public userNow(userCode: string): CubiqTS.UserImpl | null {
    let userLookupNow: UserLookupService = this.userLookup.getBase();
    if (
      typeof userCode === "string" &&
      userCode !== "" &&
      userLookupNow instanceof UserLookupService
    ) {
      let userData: CubiqTS.UserImpl = userLookupNow.getUser(userCode);
      return userData instanceof CubiqTS.UserImpl ? userData : null;
    } else {
      return null;
    }
  }

  public userByIdNow(userId: CubiqTS.Long): CubiqTS.UserImpl | null {
    let userLookupNow: UserLookupService = this.userLookup.getBase();
    if (userId instanceof CubiqTS.Long && userLookupNow instanceof UserLookupService) {
      let userData: CubiqTS.UserImpl = userLookupNow.getItem(userId);
      return userData instanceof CubiqTS.UserImpl ? userData : null;
    }
    return null;
  }

  /**********************************************************************/
  /* CONTACTS  */

  public contacts(
    clientContactsIds: Array<CubiqTS.Long>,
  ): Promise<Array<CubiqTS.ClientContactImpl>> {
    return new Promise<Array<CubiqTS.ClientContactImpl>>((res, rej) => {
      if (
        clientContactsIds != null &&
        Array.isArray(clientContactsIds) &&
        clientContactsIds.length > 0
      ) {
        try {
          let promiseArray = new Array<Promise<CubiqTS.ClientContactImpl>>();
          clientContactsIds.forEach((clientContactId: CubiqTS.Long) => {
            promiseArray.push(
              this.contact(clientContactId).catch((error) => {
                return null;
              }),
            );
          });
          Promise.all(promiseArray).then(
            (values: Array<CubiqTS.ClientContactImpl>) => {
              res(values);
            },
            (reason) => {
              rej(`Failed to resolve ${reason}`);
            },
          );
        } catch (ex) {
          rej(`Exception: ${ex}`);
        }
      } else {
        res(null);
      }
    });
  }
  public contact(clientContactId: CubiqTS.Long): Promise<CubiqTS.ClientContactImpl> {
    return new Promise<CubiqTS.ClientContactImpl>((res, rej) => {
      try {
        if (
          clientContactId != null &&
          clientContactId instanceof CubiqTS.Long &&
          clientContactId.toString().length > 0
        ) {
          this.clientLookup
            .getClientContact(clientContactId.toString())
            .then((clientContact: CubiqTS.ClientContactImpl) => {
              if (clientContact != null && clientContact instanceof CubiqTS.ClientContactImpl) {
                res(clientContact);
              } else {
                rej(
                  `ClientTrader with clientContactId: '${clientContactId.toString()}' not found.`,
                );
              }
            });
        } else {
          rej(`Invalid clientContactId: '${clientContactId}'`);
        }
      } catch (ex) {
        rej(`Exception: ${ex}`);
      }
    });
  }

  /**********************************************************************/
  /* CCY PAIR  */

  public ccyPair(productCode: string): CubiqTS.CurrencyPairImpl {
    if (typeof productCode === "string" && productCode.length === 6) {
      return this.ccyPairLookup.getBase().getItem(productCode);
    } else {
      return null;
    }
  }

  public ccyPairPromise(productCode: string): Promise<CubiqTS.CurrencyPairImpl> {
    return new Promise<CubiqTS.CurrencyPairImpl>((res, rej) => {
      if (productCode != null && typeof productCode === "string" && productCode.length === 6) {
        let currencyPairLookup: CcyPairLookupService = this.ccyPairLookup.getBase();
        let model = currencyPairLookup.getItem(productCode);

        if (model != null && model instanceof CubiqTS.CurrencyPairImpl) {
          res(model);
        } else {
          // invert
          model = currencyPairLookup.getCcyPair(productCode.substr(3, 3), productCode.substr(0, 3));
          if (model != null && model instanceof CubiqTS.CurrencyPairImpl) {
            res(model);
          } else {
            rej(`CcyPairModel with productCode: '${productCode}' not found.`);
          }
        }
      } else {
        rej(`Invalid productCode: '${productCode}'`);
      }
    });
  }

  public ccyPairPrecision(productCode: string): number {
    let length: number = 4;
    if (productCode != null && typeof productCode === "string") {
      let currencyPairLookup: CcyPairLookupService = this.ccyPairLookup.getBase();
      let pair: CubiqTS.CurrencyPairImpl = currencyPairLookup.getItem(productCode.toUpperCase());

      if (pair == null && productCode.length === 6) {
        let product: string = productCode.substr(3, 3) + productCode.substr(0, 3);
        pair = currencyPairLookup.getItem(product.toUpperCase());
        if (pair != null) {
          length = pair.limitEntrySpotInvertDecimals;
        }
      } else if (pair != null) {
        length = pair.limitEntrySpotDecimals;
      }
      //this.emptyValue = new BigNumber(0).toFixed(this.length);
    }
    return length;
  }

  public ccyPairFwdPrecision(productCode: string): number {
    let length: number = this.ccyPairPrecision(productCode);

    if (productCode != null && typeof productCode === "string") {
      let currencyPairLookup: CcyPairLookupService = this.ccyPairLookup.getBase();
      let pair: CubiqTS.CurrencyPairImpl = currencyPairLookup.getItem(productCode.toUpperCase());

      if (pair == null && productCode.length === 6) {
        let product: string = productCode.substr(3, 3) + productCode.substr(0, 3);
        pair = currencyPairLookup.getItem(product.toUpperCase());
        if (pair != null && length < pair.fwdInvertDecimalsQty) {
          length = pair.fwdInvertDecimalsQty;
        }
      } else if (pair != null && length < pair.fwdRateDecimalsQty) {
        length = pair.fwdRateDecimalsQty;
      }
    }

    return length;
  }

  public ccyPairMinTSL(productCode: string): CubiqTS.BigNumber {
    let length: CubiqTS.BigNumber = new CubiqTS.BigNumber(2);
    if (productCode != null && typeof productCode === "string") {
      let currencyPairLookup: CcyPairLookupService = this.ccyPairLookup.getBase();
      let pair: CubiqTS.CurrencyPairImpl = currencyPairLookup.getItem(productCode.toUpperCase());

      if (pair == null && productCode.length === 6) {
        let product: string = productCode.substr(3, 3) + productCode.substr(0, 3);
        pair = currencyPairLookup.getItem(product.toUpperCase());
        if (pair != null) {
          length = new CubiqTS.BigNumber(pair.trailingStopMinInvPips.toString());
        }
      } else if (pair != null) {
        length = new CubiqTS.BigNumber(pair.trailingStopMinPips.toString());
      }
      //this.emptyValue = new BigNumber(0).toFixed(this.length);
    }
    return length;
  }

  /**********************************************************************/
  /* TRADER GROUP */

  public traderGroupPromise(traderGroupId: CubiqTS.Long): Promise<CubiqTS.TradingGroupImpl> {
    return new Promise<CubiqTS.TradingGroupImpl>((res, rej) => {
      try {
        if (
          traderGroupId != null &&
          traderGroupId instanceof CubiqTS.Long &&
          traderGroupId.toString().length > 0
        ) {
          let lookup: TraderGroupLookupService = this.traderGroupLookup.getBase();
          let model: CubiqTS.TradingGroupImpl = lookup.getItem(traderGroupId.toString());
          if (model != null) {
            res(model);
          } else {
            rej(`TraderGroup with traderGroupId: '${traderGroupId.toString()}' not found.`);
          }
        } else {
          rej(`Invalid traderGroupId: '${traderGroupId}'`);
        }
      } catch (ex) {
        rej(`Exception: ${ex}`);
      }
    });
  }

  public traderGroupNow(traderGroupId: CubiqTS.Long | string): CubiqTS.TradingGroupImpl {
    if (traderGroupId instanceof CubiqTS.Long) {
      traderGroupId = traderGroupId.toString();
    }

    if (typeof traderGroupId === "string" && traderGroupId.length > 0) {
      let lookup: TraderGroupLookupService = this.traderGroupLookup.getBase();
      if (lookup != null) {
        return lookup.getItem(traderGroupId);
      }
    }
    return null;
  }

  public determineExternalTraderGroupNow(traderGroupCode: Array<string> | string): boolean {
    traderGroupCode =
      typeof traderGroupCode === "string"
        ? traderGroupCode
        : Array.isArray(traderGroupCode)
        ? traderGroupCode[0]
        : null;
    if (typeof traderGroupCode === "string") {
      let traderGroup = this.traderGroupNow(traderGroupCode);
      if (traderGroup instanceof CubiqTS.TradingGroupImpl) {
        return traderGroup.isExternallyFilledFg;
      }
    }
    return false;
  }
  /**********************************************************************/
  /* ORDER DATA from OrderDataLookupService*/

  public determineExternalTraderGroupFromBaseLookupNow(orderId: string): boolean {
    if (typeof orderId === "string") {
      let orderData = this.getOrderDataFromBaseLookupNow(orderId);
      if (orderData instanceof CubiqTS.OrderDataImpl && orderData.deskId instanceof CubiqTS.Long) {
        return this.determineExternalTraderGroupNow(orderData.deskId.toString());
      } else {
        logger.out.warn(
          `ERROR: oeLookup.determineExternalTraderGroupFromBaseLookupNow(): OrderData not found, or traderGroup not set`,
        );
      }
    } else {
      logger.out.warn(
        `ERROR: oeLookup.determineExternalTraderGroupFromBaseLookupNow(): Invalid argument orderId (${orderId})`,
      );
    }
    return null;
  }

  public getOrderDataFromBaseLookupNow(orderId: string): CubiqTS.OrderDataImpl {
    //let lookup: OrderDataLookupService = this.traderGroupLookup.getBase();
    let orderData = this.orderDataLookup.getOrderData(orderId);
    return orderData instanceof CubiqTS.OrderDataImpl ? orderData : null;
  }

  /**********************************************************************/
  /* ALGO STRATEGY */

  public algoStrategy(id: string): Promise<CubiqTS.AlgoStrategyImpl> {
    return new Promise<CubiqTS.AlgoStrategyImpl>((res, rej) => {
      this.algoLookup.isPrimed().then(() => {
        this.algoLookup.getAlgoStrategy(id).then(
          (result: CubiqTS.AlgoStrategyImpl) => {
            res(result);
          },
          (reject) => {
            logger.out.error(`ERROR: oeLookup.algoStrategy(): AlgoStrategy not found for '${id}'`);
            res(null);
          },
        );
      });
    });
  }

  /**********************************************************************/
  /* BRANCH */

  public branch(id: string): CubiqTS.BranchImpl {
    return this.branchLookupService.getBranch(id);
  }

  public isBranchBenchmarkEnabled(branchCd: string): boolean {
    let branch: CubiqTS.BranchImpl = this.branchLookupService.getItem(branchCd);
    return branch instanceof CubiqTS.BranchImpl && branch.allowBenchmarkOrdersFg;
  }

  /**********************************************************************/
  /* Fixing Source */
  public fixingSource(fixingSourcCd: string, pair: string): Promise<any> {
    return new Promise<CubiqTS.ClientImpl>((res, rej) => {
      this.fixingSourceLookup.getFixingSourceInPair(fixingSourcCd, pair).then((value) => {
        res(value);
      });
    });
  }
}
